import { CheckIcon, CopyIcon, QuestionIcon, XIcon } from "@phosphor-icons/react";
import { Badge } from "@cloudflare/kumo/components/badge";
import { Button } from "@cloudflare/kumo/components/button";
import { Select } from "@cloudflare/kumo/components/select";
import { Text } from "@cloudflare/kumo/components/text";
import { Tooltip } from "@cloudflare/kumo/components/tooltip";
import { Loader } from "@cloudflare/kumo/components/loader";
import { useEffect, useState, type ReactNode } from "react";
import type { DataConfidence, TrafficStatus } from "@juicytraffic/contracts";

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header kumo-page-header reveal">
      <div className="flex flex-col gap-1">
        {eyebrow && <Text as="span" variant="mono-secondary">{eyebrow}</Text>}
        <Text as="h1" variant="heading" size="lg">{title}</Text>
        {description && <Text variant="secondary">{description}</Text>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function FullPageLoading({ label = "Loading…" }: { label?: string }) {
  return (
    <section className="kumo-full-page-loading" role="status" aria-live="polite">
      <Loader size="lg" aria-label={label} />
      <span>{label}</span>
    </section>
  );
}

export function InfoTip({ children, label = "More information" }: { children: ReactNode; label?: string }) {
  return <Tooltip content={children} render={<Button className="kumo-info-tip" variant="ghost" shape="square" size="xs" type="button" aria-label={label} icon={QuestionIcon} />} />;
}

export type DashboardWindow = "24h" | "7d" | "30d";

export function trafficStatusLabel(status: TrafficStatus) {
  return status === "valid" ? "Valid" : status === "suspicious" ? "Suspicious" : "Invalid";
}

export function TimeRange({
  value = "24h",
  onChange,
}: {
  value?: DashboardWindow;
  onChange?: (value: DashboardWindow) => void;
}) {
  return (
    <Select
      aria-label="Reporting period"
      size="sm"
      className="time-range-control"
      value={value}
      onValueChange={(next) => next && onChange?.(next as DashboardWindow)}
      items={{ "24h": "Last 24 hours", "7d": "Last 7 days", "30d": "Last 30 days" }}
    />
  );
}

export function TrafficStatusBadge({ status }: { status: TrafficStatus }) {
  const variant = status === "valid" ? "green" : status === "suspicious" ? "orange" : "red";
  return <Badge variant={variant}>{trafficStatusLabel(status)}</Badge>;
}

export function ConfidenceBadge({ value }: { value: DataConfidence }) {
  const variant = value === "high" ? "success" : value === "medium" ? "warning" : "neutral";
  return <Badge variant={variant} className={`confidence confidence-${value}`}>{value}</Badge>;
}

export function EmptyLine({ width = "70%" }: { width?: string }) {
  return <span className="empty-line" style={{ width }} />;
}

export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
  };
  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timeout);
  }, [copied]);
  return (
    <Button size="xs" variant="secondary" type="button" onClick={copy} icon={copied ? CheckIcon : CopyIcon}>
      {copied ? "Copied" : label}
    </Button>
  );
}

export function Toast({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const timeout = setTimeout(onClose, 3200);
    return () => clearTimeout(timeout);
  }, [onClose]);
  return (
    <div className="toast" role="status" aria-live="polite">
      <span className="toast-check">
        <CheckIcon size={14} />
      </span>
      <span>{children}</span>
      <Button variant="ghost" shape="square" size="xs" onClick={onClose} aria-label="Close notification" icon={XIcon} />
    </div>
  );
}
